import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Customer } from '../../types/customer';
import { useAuth } from '../../contexts/AuthContext';

interface CustomerRelationshipLogFormProps {
  customer: Customer;
  onAddLog: (content: string) => Promise<void>;
  onCancel: () => void;
  initialContent?: string;
  isEditing?: boolean;
}

const CustomerRelationshipLogForm: React.FC<CustomerRelationshipLogFormProps> = ({
  customer,
  onAddLog,
  onCancel,
  initialContent = '',
  isEditing = false
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [content, setContent] = useState(initialContent);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Update content if initialContent changes (e.g. when switching between edit entries)
  useEffect(() => {
    setContent(initialContent);
  }, [initialContent]);

  const maxLength = 1000;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) {
      setError(t('customers.relationshipLog.contentRequired') || 'Content is required');
      return;
    }

    if (content.length > maxLength) {
      setError(t('customers.relationshipLog.maxLength') || `Maximum ${maxLength} characters`);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await onAddLog(content);
      setContent('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Generate example entry ID for display purposes
  const generateExampleEntryId = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}${month}${day}`;
    const seq = '0001';
    
    let prefix = '';
    if (customer.type === 'organization' && customer.taxNumber) {
      prefix = customer.taxNumber.substring(0, 8);
    } else {
      prefix = customer.id.substring(0, 8);
    }
    
    return `${prefix}_${dateStr}_${seq}`;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-200 mb-3">
        {isEditing
          ? (t('customers.relationshipLog.edit') || 'Edit log entry')
          : (t('customers.relationshipLog.add') || 'Add new log entry')}
      </h3>
      
      {!isEditing && (
        <div className="mb-4">
          <div className="flex items-center mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-2">
              {t('customers.relationshipLog.entryId') || 'Entry ID'}:
            </span>
            <span className="text-sm text-gray-600 dark:text-gray-400 font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
              {generateExampleEntryId()}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">(auto-generated)</span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label
            htmlFor="log-content"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            {t('customers.relationshipLog.content') || 'Content'}
          </label>
          <textarea
            id="log-content"
            rows={4}
            className={`w-full rounded-md border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 ${
              error ? 'border-red-300 dark:border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:ring-primary-500'
            } shadow-sm p-2 text-sm`}
            placeholder={t('customers.relationshipLog.contentPlaceholder') || 'Enter log content...'}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            maxLength={maxLength}
            disabled={isSubmitting}
          />
          <div className="flex justify-between mt-1">
            {error ? (
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('customers.relationshipLog.maxLength') || `Maximum ${maxLength} characters`}
              </p>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {content.length}/{maxLength}
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-2">
          <button
            type="button"
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            {t('customers.relationshipLog.cancel') || 'Cancel'}
          </button>
          <button
            type="submit"
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t('common.submitting') || 'Saving...'
              : isEditing
                ? t('customers.relationshipLog.update') || 'Update entry'
                : t('customers.relationshipLog.save') || 'Save entry'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomerRelationshipLogForm; 